import { c as _c } from "react/compiler-runtime";
import CustomTooltip from "@/components/utils/tooltip/CustomTooltip";
import type { MetricData } from "@/hooks/useCommunityMetrics";
import { ArrowTopRightOnSquareIcon } from "@heroicons/react/24/outline";
import Link from "next/link";
import type { ReactNode } from "react";
import { formatCompactNumber, formatNumberWithCommas, formatPercent } from "../utils/formatNumbers";
interface MetricCardProps {
  readonly title: string;
  readonly dailyData: MetricData;
  readonly weeklyData: MetricData;
  readonly icon: ReactNode;
  readonly iconBgColor: string;
  readonly accentColor: string;
  readonly useValueCount?: boolean;
  readonly suffix?: string | undefined;
  readonly href?: string;
}
function StatBlock(t0) {
  const $ = _c(37);
  const {
    label,
    currentValue,
    previousValue,
    previousLabel,
    changePercent,
    accentColor,
    isPrimary: t1,
    suffix
  } = t0;
  const isPrimary = t1 === undefined ? false : t1;
  const isPositive = changePercent !== null && changePercent >= 0;
  const colorClasses = isPositive ? "tw-bg-green/20 tw-text-green" : "tw-bg-red/20 tw-text-red";
  const badgeClasses = changePercent === null ? "tw-bg-iron-800 tw-text-iron-400" : colorClasses;
  const t2 = `tw-mb-1 tw-text-xs tw-font-medium tw-uppercase tw-tracking-wider ${accentColor}`;
  let t3;
  if ($[0] !== label || $[1] !== t2) {
    t3 = <p className={t2}>{label}</p>;
    $[0] = label;
    $[1] = t2;
    $[2] = t3;
  } else {
    t3 = $[2];
  }
  let t4;
  if ($[3] !== currentValue) {
    t4 = formatNumberWithCommas(currentValue);
    $[3] = currentValue;
    $[4] = t4;
  } else {
    t4 = $[4];
  }
  const t5 = `${t4}${suffix ?? ""}`;
  const t6 = `tw-cursor-default tw-font-bold tw-text-white ${isPrimary ? "tw-text-4xl" : "tw-text-2xl"}`;
  let t7;
  if ($[5] !== currentValue) {
    t7 = formatCompactNumber(currentValue);
    $[5] = currentValue;
    $[6] = t7;
  } else {
    t7 = $[6];
  }
  let t8;
  if ($[7] !== suffix || $[8] !== t6 || $[9] !== t7) {
    t8 = <p className={t6}>{t7}{suffix}</p>;
    $[7] = suffix;
    $[8] = t6;
    $[9] = t7;
    $[10] = t8;
  } else {
    t8 = $[10];
  }
  let t9;
  if ($[11] !== t5 || $[12] !== t8) {
    t9 = <CustomTooltip content={t5} placement="top">{t8}</CustomTooltip>;
    $[11] = t5;
    $[12] = t8;
    $[13] = t9;
  } else {
    t9 = $[13];
  }
  let t10;
  if ($[14] !== previousValue) {
    t10 = formatNumberWithCommas(previousValue);
    $[14] = previousValue;
    $[15] = t10;
  } else {
    t10 = $[15];
  }
  const t11 = `${t10}${suffix ?? ""}`;
  let t12;
  if ($[16] !== previousValue) {
    t12 = formatCompactNumber(previousValue);
    $[16] = previousValue;
    $[17] = t12;
  } else {
    t12 = $[17];
  }
  let t13;
  if ($[18] !== suffix || $[19] !== t12) {
    t13 = <span className="tw-cursor-default">{t12}{suffix}</span>;
    $[18] = suffix;
    $[19] = t12;
    $[20] = t13;
  } else {
    t13 = $[20];
  }
  let t14;
  if ($[21] !== t11 || $[22] !== t13) {
    t14 = <CustomTooltip content={t11} placement="top">{t13}</CustomTooltip>;
    $[21] = t11;
    $[22] = t13;
    $[23] = t14;
  } else {
    t14 = $[23];
  }
  let t15;
  if ($[24] !== previousLabel || $[25] !== t14) {
    t15 = <p className="tw-mt-1 tw-text-xs tw-text-neutral-500">vs{" "}{t14}{" "}{previousLabel}</p>;
    $[24] = previousLabel;
    $[25] = t14;
    $[26] = t15;
  } else {
    t15 = $[26];
  }
  const t16 = `tw-mt-2 tw-inline-block tw-rounded tw-px-2 tw-py-0.5 tw-text-sm tw-font-semibold ${badgeClasses}`;
  let t17;
  if ($[27] !== changePercent) {
    t17 = formatPercent(changePercent);
    $[27] = changePercent;
    $[28] = t17;
  } else {
    t17 = $[28];
  }
  let t18;
  if ($[29] !== t16 || $[30] !== t17) {
    t18 = <span className={t16}>{t17}</span>;
    $[29] = t16;
    $[30] = t17;
    $[31] = t18;
  } else {
    t18 = $[31];
  }
  let t19;
  if ($[32] !== t15 || $[33] !== t18 || $[34] !== t3 || $[35] !== t9) {
    t19 = <div className="tw-flex-1">{t3}{t9}{t15}{t18}</div>;
    $[32] = t15;
    $[33] = t18;
    $[34] = t3;
    $[35] = t9;
    $[36] = t19;
  } else {
    t19 = $[36];
  }
  return t19;
}
export default function MetricCard(t0) {
  const $ = _c(56);
  const {
    title,
    dailyData,
    weeklyData,
    icon,
    iconBgColor,
    accentColor,
    useValueCount: t1,
    suffix,
    href
  } = t0;
  const useValueCount = t1 === undefined ? false : t1;
  let t2;
  if ($[0] !== useValueCount) {
    t2 = (data, period) => useValueCount ? data[period].valueCount : data[period].eventCount;
    $[0] = useValueCount;
    $[1] = t2;
  } else {
    t2 = $[1];
  }
  const getCount = t2;
  let t3;
  if ($[2] !== useValueCount) {
    t3 = data_0 => useValueCount ? data_0.valueCountChangePercent : data_0.eventCountChangePercent;
    $[2] = useValueCount;
    $[3] = t3;
  } else {
    t3 = $[3];
  }
  const getChangePercent = t3;
  const t4 = `tw-block tw-h-full tw-rounded-xl tw-border tw-border-neutral-800 tw-bg-[#0f1318] tw-p-5 tw-transition-all tw-duration-300 ${href ? "hover:-tw-translate-y-1 hover:tw-border-neutral-700 hover:tw-shadow-xl hover:tw-shadow-neutral-900/50" : ""}`;
  let t5;
  if ($[4] !== href) {
    t5 = href && <ArrowTopRightOnSquareIcon className="tw-size-4 tw-text-neutral-500" />;
    $[4] = href;
    $[5] = t5;
  } else {
    t5 = $[5];
  }
  let t6;
  if ($[6] !== t5 || $[7] !== title) {
    t6 = <h3 className="tw-flex tw-items-center tw-gap-2 tw-text-base tw-font-semibold tw-text-white">{title}{t5}</h3>;
    $[6] = t5;
    $[7] = title;
    $[8] = t6;
  } else {
    t6 = $[8];
  }
  const t7 = `tw-flex tw-size-10 tw-items-center tw-justify-center tw-rounded-lg ${iconBgColor}`;
  let t8;
  if ($[9] !== icon || $[10] !== t7) {
    t8 = <div className={t7}>{icon}</div>;
    $[9] = icon;
    $[10] = t7;
    $[11] = t8;
  } else {
    t8 = $[11];
  }
  let t9;
  if ($[12] !== t6 || $[13] !== t8) {
    t9 = <div className="tw-mb-5 tw-flex tw-items-start tw-justify-between">{t6}{t8}</div>;
    $[12] = t6;
    $[13] = t8;
    $[14] = t9;
  } else {
    t9 = $[14];
  }
  let t10;
  if ($[15] !== dailyData || $[16] !== getCount) {
    t10 = getCount(dailyData, "current");
    $[15] = dailyData;
    $[16] = getCount;
    $[17] = t10;
  } else {
    t10 = $[17];
  }
  let t11;
  if ($[18] !== dailyData || $[19] !== getCount) {
    t11 = getCount(dailyData, "previous");
    $[18] = dailyData;
    $[19] = getCount;
    $[20] = t11;
  } else {
    t11 = $[20];
  }
  let t12;
  if ($[21] !== dailyData || $[22] !== getChangePercent) {
    t12 = getChangePercent(dailyData);
    $[21] = dailyData;
    $[22] = getChangePercent;
    $[23] = t12;
  } else {
    t12 = $[23];
  }
  let t13;
  if ($[24] !== accentColor || $[25] !== suffix || $[26] !== t10 || $[27] !== t11 || $[28] !== t12) {
    t13 = <StatBlock label="Last 24h" currentValue={t10} previousValue={t11} previousLabel="prev day" changePercent={t12} accentColor={accentColor} isPrimary={true} suffix={suffix} />;
    $[24] = accentColor;
    $[25] = suffix;
    $[26] = t10;
    $[27] = t11;
    $[28] = t12;
    $[29] = t13;
  } else {
    t13 = $[29];
  }
  let t14;
  if ($[30] === Symbol.for("react.memo_cache_sentinel")) {
    t14 = <div className="tw-w-px tw-bg-iron-700/50" />;
    $[30] = t14;
  } else {
    t14 = $[30];
  }
  let t15;
  if ($[31] !== getCount || $[32] !== weeklyData) {
    t15 = getCount(weeklyData, "current");
    $[31] = getCount;
    $[32] = weeklyData;
    $[33] = t15;
  } else {
    t15 = $[33];
  }
  let t16;
  if ($[34] !== getCount || $[35] !== weeklyData) {
    t16 = getCount(weeklyData, "previous");
    $[34] = getCount;
    $[35] = weeklyData;
    $[36] = t16;
  } else {
    t16 = $[36];
  }
  let t17;
  if ($[37] !== getChangePercent || $[38] !== weeklyData) {
    t17 = getChangePercent(weeklyData);
    $[37] = getChangePercent;
    $[38] = weeklyData;
    $[39] = t17;
  } else {
    t17 = $[39];
  }
  let t18;
  if ($[40] !== accentColor || $[41] !== suffix || $[42] !== t15 || $[43] !== t16 || $[44] !== t17) {
    t18 = <StatBlock label="Last 7 Days" currentValue={t15} previousValue={t16} previousLabel="prev week" changePercent={t17} accentColor={accentColor} suffix={suffix} />;
    $[40] = accentColor;
    $[41] = suffix;
    $[42] = t15;
    $[43] = t16;
    $[44] = t17;
    $[45] = t18;
  } else {
    t18 = $[45];
  }
  let t19;
  if ($[46] !== t13 || $[47] !== t18) {
    t19 = <div className="tw-flex tw-gap-6">{t13}{t14}{t18}</div>;
    $[46] = t13;
    $[47] = t18;
    $[48] = t19;
  } else {
    t19 = $[48];
  }
  let t20;
  if ($[49] !== t19 || $[50] !== t4 || $[51] !== t9) {
    t20 = <div className={t4}>{t9}{t19}</div>;
    $[49] = t19;
    $[50] = t4;
    $[51] = t9;
    $[52] = t20;
  } else {
    t20 = $[52];
  }
  const content = t20;
  if (href) {
    let t21;
    if ($[53] !== content || $[54] !== href) {
      t21 = <Link href={href} className="tw-block tw-no-underline hover:tw-no-underline">{content}</Link>;
      $[53] = content;
      $[54] = href;
      $[55] = t21;
    } else {
      t21 = $[55];
    }
    return t21;
  }
  return content;
}